import { Routes } from '@angular/router';
import {
  defaultLocaleGuard,
  englishLocaleGuard,
  portugueseLocaleGuard,
} from '@core/i18n/locale-route.guards';
import { PLACEHOLDER_ROUTES } from '@core/routing/route-paths';
import { SiteLayoutComponent } from '@layout/components/site-layout/site-layout.component';

const placeholder = (pageTitleKey: string) => ({
  loadComponent: () =>
    import('@features/placeholder/placeholder.page').then((m) => m.PlaceholderPage),
  data: { pageTitleKey },
});

const siteChildren = (): Routes => [
  {
    path: '',
    loadComponent: () => import('@features/home/home.page').then((m) => m.HomePage),
    data: { headerVariant: 'dark' },
  },
  {
    path: 'quem-somos',
    loadComponent: () =>
      import('@features/quem-somos/quem-somos.page').then((m) => m.QuemSomosPage),
  },
  {
    path: 'servicos',
    loadComponent: () => import('@features/servicos/servicos.page').then((m) => m.ServicosPage),
  },
  {
    path: 'ambiente-remoto-pesquisa',
    loadComponent: () => import('@features/arp/arp.page').then((m) => m.ArpPage),
  },
  {
    path: 'regbr',
    loadComponent: () => import('@features/regbr/regbr.page').then((m) => m.RegbrPage),
  },
  {
    path: 'regbr/metricas',
    loadComponent: () =>
      import('@features/regbr/regbr-metricas.page').then((m) => m.RegbrMetricasPage),
    children: [
      {
        path: '',
        redirectTo: 'complexidade-linguistica',
        pathMatch: 'full',
      },
      {
        path: 'complexidade-linguistica',
        loadComponent: () =>
          import('@features/regbr/metricas/complexidade-linguistica.page').then(
            (m) => m.ComplexidadeLinguisticaPage,
          ),
      },
      {
        path: 'influencia-industria',
        loadComponent: () =>
          import('@features/regbr/metricas/influencia-industria.page').then(
            (m) => m.InfluenciaIndustriaPage,
          ),
      },
      {
        path: 'popularidade',
        loadComponent: () =>
          import('@features/regbr/metricas/popularidade.page').then((m) => m.PopularidadePage),
      },
      {
        path: 'restritividade',
        loadComponent: () =>
          import('@features/regbr/metricas/restritividade.page').then((m) => m.RestritividadePage),
      },
    ],
  },
  {
    path: 'nuvem-perguntas',
    loadComponent: () =>
      import('@features/nuvem/nuvem-perguntas.page').then((m) => m.NuvemPerguntasPage),
  },
  {
    path: 'perguntas/:id',
    loadComponent: () =>
      import('@features/nuvem/pergunta-generica.page').then((m) => m.PerguntaGenericaPage),
  },
  {
    path: 'datacuidados',
    loadComponent: () =>
      import('@features/data-cuidados/data-cuidados.page').then((m) => m.DataCuidadosPage),
    data: { pageTitleKey: 'nav.items.dataCuidados' },
  },
  {
    path: 'datacuidados/dados',
    loadComponent: () =>
      import('@features/data-cuidados/data-cuidados-dados.page').then((m) => m.DataCuidadosDadosPage),
    data: { pageTitleKey: 'nav.items.dataCuidados' },
  },
  {
    path: 'datacuidados/dados/:indicadorId',
    loadComponent: () =>
      import('@features/data-cuidados/data-cuidados-dados.page').then((m) => m.DataCuidadosDadosPage),
    data: { pageTitleKey: 'nav.items.dataCuidados' },
  },
  {
    path: 'igualdade-racial',
    loadComponent: () =>
      import('@features/igualdade-racial/igualdade-racial.page').then(
        (m) => m.IgualdadeRacialPage,
      ),
    data: { pageTitleKey: 'nav.items.racialEquality' },
  },
  {
    path: 'igualdade-racial/:slug',
    loadComponent: () =>
      import('@features/nuvem/pergunta-generica.page').then((m) => m.PerguntaGenericaPage),
    data: { platform: 'mir', pageTitleKey: 'nav.items.racialEquality' },
  },
  {
    path: 'datastories',
    loadComponent: () =>
      import('@features/datastories/datastories.page').then((m) => m.DatastoriesPage),
  },
  {
    path: 'datastories/transferencias-voluntarias',
    loadComponent: () =>
      import('@features/datastories/transferencias-voluntarias/transferencias-voluntarias.page').then(
        (m) => m.TransferenciasVoluntariasPage,
      ),
  },
  {
    path: 'datastories/favelas',
    loadComponent: () =>
      import('@features/datastories/favelas/favelas.page').then((m) => m.FavelasPage),
  },
  {
    path: 'datastories/queimadas',
    loadComponent: () =>
      import('@features/datastories/queimadas/queimadas.page').then((m) => m.QueimadasPage),
  },
  {
    path: 'datastories/mudanca-cargo',
    loadComponent: () =>
      import('@features/datastories/mudanca-cargo/mudanca-cargo.page').then(
        (m) => m.MudancaCargoPage,
      ),
  },
  {
    path: 'datastories/mobilidade-social',
    loadComponent: () =>
      import('@features/datastories/mobilidade-social/mobilidade-social.page').then(
        (m) => m.MobilidadeSocialPage,
      ),
  },
  {
    path: 'datastories/genero',
    loadComponent: () =>
      import('@features/datastories/genero/genero.page').then((m) => m.GeneroPage),
  },
  {
    path: 'datastories/cotas',
    loadComponent: () => import('@features/datastories/cotas/cotas.page').then((m) => m.CotasPage),
  },
  {
    path: 'datastories/brasil-solar',
    loadComponent: () =>
      import('@features/datastories/brasil-solar/brasil-solar.page').then((m) => m.BrasilSolarPage),
  },
  {
    path: 'datastories/transicao-energia',
    loadComponent: () =>
      import('@features/datastories/transicao-energia/transicao-energia.page').then(
        (m) => m.TransicaoEnergiaPage,
      ),
  },
  {
    path: 'datastories/producao-normativa',
    loadComponent: () =>
      import('@features/datastories/producao-normativa/producao-normativa.page').then(
        (m) => m.ProducaoNormativaPage,
      ),
  },
  {
    path: 'datastories/cargos-confianca',
    loadComponent: () =>
      import('@features/datastories/cargos-confianca/cargos-confianca.page').then(
        (m) => m.CargosConfiancaPage,
      ),
  },
  {
    path: 'datastories/analise-compras',
    loadComponent: () =>
      import('@features/datastories/analise-compras/analise-compras.page').then(
        (m) => m.AnaliseComprasPage,
      ),
  },
  {
    path: 'datastories/servicos-automacao',
    loadComponent: () =>
      import('@features/datastories/servicos-automacao/servicos-automacao.page').then(
        (m) => m.ServicosAutomacaoPage,
      ),
  },
  {
    path: 'datastories/regbr/complexidade',
    loadComponent: () =>
      import('@features/datastories/regbr/complexidade/complexidade.page').then(
        (m) => m.RegbrComplexidadePage,
      ),
  },
  {
    path: 'datastories/regbr/popularidade',
    loadComponent: () =>
      import('@features/datastories/regbr/popularidade/popularidade.page').then(
        (m) => m.RegbrPopularidadePage,
      ),
  },
  {
    path: 'datastories/regbr/influencia',
    loadComponent: () =>
      import('@features/datastories/regbr/influencia/influencia.page').then(
        (m) => m.RegbrInfluenciaPage,
      ),
  },
  {
    path: 'datastories/regbr/restritividade',
    loadComponent: () =>
      import('@features/datastories/regbr/restritividade/restritividade.page').then(
        (m) => m.RegbrRestritividadePage,
      ),
  },
  {
    path: 'evidencia-express',
    loadComponent: () =>
      import('@features/evidencia-express/evidencia-express.page').then(
        (m) => m.EvidenciaExpressPage,
      ),
  },
  {
    path: 'eventos',
    loadComponent: () => import('@features/eventos/eventos.page').then((m) => m.EventosPage),
  },
  {
    path: 'relatorios',
    loadComponent: () =>
      import('@features/relatorios/relatorios.page').then((m) => m.RelatoriosPage),
  },
  {
    // Caminho mantido igual ao valor gravado em `relatorio.link_publicacao`
    // no backend ("/evex/mudancas-clima"), não sob /evidencia-express.
    path: 'evex/mudancas-clima',
    loadComponent: () =>
      import('@features/evidencia-express/mudancas-clima/mudancas-clima.page').then(
        (m) => m.MudancasClimaPage,
      ),
  },
  ...PLACEHOLDER_ROUTES.filter(
    ({ path }) =>
      ![
        'quem-somos',
        'servicos',
        'ambiente-remoto-pesquisa',
        'regbr',
        'regbr/metricas',
        'nuvem-perguntas',
        'perguntas/:slug',
        'igualdade-racial',
        'igualdade-racial/:slug',
        'datastories',
        'evidencia-express',
        'eventos',
        'relatorios',
      ].includes(path),
  ).map(({ path, pageTitleKey }) => ({
    path,
    ...placeholder(pageTitleKey),
  })),
  {
    path: '**',
    loadComponent: () => import('@features/not-found/not-found.page').then((m) => m.NotFoundPage),
  },
];

export const routes: Routes = [
  {
    path: 'en',
    canActivate: [englishLocaleGuard],
    component: SiteLayoutComponent,
    children: siteChildren(),
  },
  {
    path: 'pt-br',
    canActivate: [portugueseLocaleGuard],
    component: SiteLayoutComponent,
    children: siteChildren(),
  },
  {
    path: '',
    canActivate: [defaultLocaleGuard],
    component: SiteLayoutComponent,
    children: siteChildren(),
  },
];
